// services/presenceService.js
import { doc, setDoc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, onValue, onDisconnect, set, get } from 'firebase/database';
import { db, database } from '../config/firebasedeconfig';

/**
 * Service centralisé pour gérer la présence des utilisateurs en temps réel
 * Utilise à la fois Firestore et Realtime Database pour un suivi fiable
 */
class PresenceService {
  
  /**
   * Vérifie si Realtime Database est disponible
   */
  isDatabaseAvailable() {
    return database && database._type !== 'mock-database';
  }
  
  /**
   * Initialise le suivi de présence pour un utilisateur
   * @param {string} userId - ID de l'utilisateur à suivre
   * @param {object} metadata - Métadonnées additionnelles (conversationId, etc.)
   * @returns {Function} - Fonction de nettoyage pour arrêter le suivi
   */
  initializePresence(userId, metadata = {}) {
    if (!userId) {
      console.error('[PRESENCE] Impossible d\'initialiser la présence: userId manquant');
      return () => {};
    }
    
    console.log(`[PRESENCE] Initialisation de la présence pour ${userId}`);
    
    // Si Realtime Database n'est pas disponible, utiliser seulement Firestore
    if (!this.isDatabaseAvailable()) {
      console.warn('[PRESENCE] Mode dégradé: Realtime Database non disponible, utilisation de Firestore uniquement');
      return this.initializeFirestoreOnly(userId, metadata);
    }
    
    // Références aux deux bases de données
    const firestoreRef = doc(db, 'status', userId);
    const realtimeRef = ref(database, `online/${userId}`);
    const connectedRef = ref(database, '.info/connected');
    
    // Données à enregistrer
    const presenceData = {
      status: 'online',
      lastSeen: serverTimestamp(),
      ...metadata
    };
    
    // Fonction pour mettre à jour le statut dans les deux bases
    const updateStatus = (status) => {
      const statusData = {
        ...presenceData,
        status,
        lastSeen: serverTimestamp()
      };
      
      // Mettre à jour Realtime Database
      set(realtimeRef, statusData).catch(err => {
        console.error(`[PRESENCE] Erreur mise à jour RTDB ${status}:`, err);
      });
      
      // Mettre à jour Firestore
      const firestorePromise = status === 'online' 
        ? setDoc(firestoreRef, statusData, { merge: true })
        : updateDoc(firestoreRef, statusData);
      
      firestorePromise.catch(err => {
        console.error(`[PRESENCE] Erreur mise à jour Firestore ${status}:`, err);
      });
    };
    
    // Configurer l'écouteur de connexion
    const connectionListener = onValue(connectedRef, (snapshot) => {
      const isConnected = snapshot.val() === true;
      console.log(`[PRESENCE] État de connexion pour ${userId}: ${isConnected ? 'Connecté' : 'Déconnecté'}`);
      
      if (isConnected) {
        // Configurer le nettoyage automatique à la déconnexion
        onDisconnect(realtimeRef).set({
          ...presenceData,
          status: 'offline',
          lastSeen: serverTimestamp()
        });
        
        // Mettre à jour le statut en ligne
        updateStatus('online');
      }
    });
    
    // Fonction de nettoyage pour arrêter le suivi
    return () => {
      console.log(`[PRESENCE] Nettoyage de la présence pour ${userId}`);
      connectionListener();
      updateStatus('offline');
    };
  }
  
  /**
   * Version dégradée utilisant seulement Firestore
   */
  initializeFirestoreOnly(userId, metadata = {}) {
    const firestoreRef = doc(db, 'status', userId);
    
    const presenceData = {
      status: 'online',
      lastSeen: serverTimestamp(),
      ...metadata
    };
    
    // Mettre à jour le statut uniquement dans Firestore
    setDoc(firestoreRef, presenceData, { merge: true }).catch(err => {
      console.error('[PRESENCE] Erreur mise à jour Firestore mode dégradé:', err);
    });
    
    // Fonction de nettoyage simplifiée
    return () => {
      console.log(`[PRESENCE] Nettoyage de la présence (mode dégradé) pour ${userId}`);
      updateDoc(firestoreRef, {
        status: 'offline',
        lastSeen: serverTimestamp()
      }).catch(err => {
        console.error('[PRESENCE] Erreur nettoyage Firestore:', err);
      });
    };
  }
  
  /**
   * Récupérer la liste des utilisateurs actuellement en ligne
   * @returns {Promise<Object[]>} - Liste des utilisateurs en ligne avec leurs métadonnées
   */
  async getOnlineUsers() {
    try {
      if (!this.isDatabaseAvailable()) {
        console.warn('[PRESENCE] Mode dégradé: impossible de récupérer les utilisateurs en ligne sans Realtime Database');
        return [];
      }
      
      const snapshot = await get(ref(database, 'online'));
      if (!snapshot.exists()) return [];
      
      const onlineData = snapshot.val();
      return Object.entries(onlineData)
        .filter(([_, data]) => data.status === 'online')
        .map(([userId, data]) => ({
          userId,
          ...data
        }));
    } catch (error) {
      console.error('[PRESENCE] Erreur récupération utilisateurs en ligne:', error);
      return [];
    }
  }
  
  /**
   * Mettre à jour manuellement le statut d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} status - Nouveau statut ('online', 'offline', 'away', etc.)
   * @param {object} metadata - Métadonnées additionnelles
   */
  async updateUserStatus(userId, status, metadata = {}) {
    if (!userId) return;
    
    const statusData = {
      status,
      lastSeen: serverTimestamp(),
      ...metadata
    };
    
    try {
      // Mettre à jour dans Realtime Database si disponible
      if (this.isDatabaseAvailable()) {
        await set(ref(database, `online/${userId}`), statusData);
      }
      
      // Mettre à jour dans Firestore
      await updateDoc(doc(db, 'status', userId), statusData);
      
      console.log(`[PRESENCE] Statut de ${userId} mis à jour: ${status}`);
    } catch (error) {
      console.error(`[PRESENCE] Erreur mise à jour statut ${userId}:`, error);
    }
  }
}

// Exporter une instance unique du service
const presenceService = new PresenceService();
export default presenceService; 